import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ACCOUNT_LABELS, type AccountKind, type AccountSummary, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { SortTh } from '../../components/SortTh';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import './invoices.css';

/** Adresse der Liste je Art. Debitoren und Kreditoren haben je eigene Seiten. */
export const accountPath = (kind: AccountKind): string => (kind === 'debtor' ? '/debitoren' : '/kreditoren');

export function AccountStateBadge({ active }: { active: boolean }) {
  return <span className={`invoice-status ${active ? 'invoice-status--issued' : 'invoice-status--cancelled'}`}>{active ? 'Aktiv' : 'Gesperrt'}</span>;
}

/**
 * Liste der Debitoren oder Kreditoren (KON-161) mit Suche, Filter nach Stand und Sortierung. Jede Art ist eine eigene Tabelle mit eigener Seite, der Kontakt dahinter
 * erscheint auch in Kontakte. Von hier aus legt man neue Konten an und öffnet die Maske eines Kontos.
 */
export function AccountsPage({ kind }: { kind: AccountKind }) {
  const label = ACCOUNT_LABELS[kind];
  const base = accountPath(kind);
  const endpoint = kind === 'debtor' ? '/debtors' : '/creditors';
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);

  const list = useQuery({
    queryKey: ['accounts', kind, { q, status, sort, page }],
    queryFn: () => api<Paginated<AccountSummary>>('GET', `${endpoint}${queryString({ q, status, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const onSort = (next: string) => update({ sort: next });
  const fields: FilterField[] = [
    {
      key: 'status',
      label: 'Stand',
      type: 'select',
      options: [{ value: '', label: 'Aktive' }, { value: 'inactive', label: 'Gesperrte' }, { value: 'all', label: 'Alle' }],
    },
  ];
  const terms = (a: AccountSummary) => (a.paymentTermsDays === null ? 'Vorgabe' : `${a.paymentTermsDays} Tage`);
  const other = kind === 'debtor' ? ACCOUNT_LABELS.creditor : ACCOUNT_LABELS.debtor;

  return (
    <>
      <div className="page-head">
        <h1>{label.plural}</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        <Link className="btn btn--primary btn--sm page-head__action" to={`${base}/neu`}>Neuer {label.singular}</Link>
      </div>
      <p className="muted small">{label.plural} sind {label.person === 'Kunde' ? 'Kunden' : 'Lieferanten'} mit einem Personenkonto in der Buchhaltung. Jeder steht auch in den Kontakten, dort legen Sie mit einem Klick ein Konto an.</p>
      <FilterBar
        search={{ value: q, placeholder: `Nummer, Name, Ort oder USt-IdNr.`, onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ status }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Nummer" sortKey="number" sort={sort} onSort={onSort} />
              <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
              <SortTh label="Ort" sortKey="city" sort={sort} onSort={onSort} />
              <th>USt-IdNr.</th>
              <th>Zahlungsziel</th>
              <th>Stand</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr><td colSpan={6} className="empty">{q || status ? `Keine ${label.plural} zu dieser Suche.` : `Noch keine ${label.plural}. Legen Sie über „Neuer ${label.singular}“ einen an oder machen Sie einen Kontakt zum ${label.singular}.`}</td></tr>
            )}
            {list.data?.data.map((a) => (
              <ExpandRow
                key={a.id}
                summary={
                  <Summary
                    title={<Link to={`${base}/${a.id}`} className="strong-link">{a.number} · {a.name}</Link>}
                    aside={<AccountStateBadge active={a.active} />}
                    line2={[a.city, a.vatId].filter(Boolean).join(' · ')}
                    line3={[`Zahlungsziel ${terms(a)}`, a.otherAccountId ? `auch ${other.singular}` : null].filter(Boolean).join(' · ')}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <Link to={`${base}/${a.id}`} className="strong-link">{a.number}</Link>
                </td>
                <td data-label="Name">
                  <Link to={`${base}/${a.id}`}>{a.name}</Link>
                  {a.otherAccountId && <div className="muted small">auch {other.singular}</div>}
                </td>
                <td data-label="Ort">{a.city ?? '–'}</td>
                <td data-label="USt-IdNr.">{a.vatId ?? '–'}</td>
                <td data-label="Zahlungsziel">{terms(a)}</td>
                <td data-label="Stand"><AccountStateBadge active={a.active} /></td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
